import { useState, type FormEvent } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, FormField } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { formatDateTime } from "@/features/outreach/format";
import { useSignPortalContract } from "./usePortal";
import type { PortalContract } from "./types";

interface PortalContractCardProps {
  contract: PortalContract;
  token: string;
}

export function PortalContractCard({ contract, token }: PortalContractCardProps) {
  if (contract.signed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Service agreement</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-success">
            Signed by {contract.signer_name ?? "you"}
            {contract.signed_at ? ` on ${formatDateTime(contract.signed_at)}` : ""}.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <PortalSignForm contract={contract} token={token} />;
}

function PortalSignForm({ token }: PortalContractCardProps) {
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const signContract = useSignPortalContract(token);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!signerName.trim()) {
      setFormError("Please enter your full name.");
      return;
    }
    if (!consent) {
      setFormError("Please confirm you agree to the service agreement and liability waiver.");
      return;
    }

    signContract.mutate(
      {
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim() || undefined,
        consent,
      },
      { onError: (err) => setFormError(getErrorMessage(err)) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service agreement</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <FormField
            label="Your full name"
            required
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Jane Smith"
            autoComplete="name"
          />
          <FormField
            label="Email"
            hint="Optional — we'll send a copy of the signed agreement here."
            type="email"
            value={signerEmail}
            onChange={(e) => setSignerEmail(e.target.value)}
            placeholder="jane@example.com"
            autoComplete="email"
          />
          <Checkbox
            label="I have read and agree to the service agreement and liability waiver"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />

          {formError && (
            <p className="text-sm text-danger" role="alert">
              {formError}
            </p>
          )}

          <Button type="submit" disabled={signContract.isPending}>
            {signContract.isPending ? "Signing…" : "Sign agreement"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
